import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
export const ORIGIN = BACKEND_URL;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Attach bearer token from localStorage as a fallback to the httpOnly cookie.
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("espresso_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export function setToken(token) {
  if (token) localStorage.setItem("espresso_token", token);
  else localStorage.removeItem("espresso_token");
}

// ---- Silent token refresh: on a 401, refresh the access token once (single-flight)
// and retry the original request. Only hard-redirect if refresh itself fails.
let refreshPromise = null;

function isAuthEndpoint(url = "") {
  return (
    url.includes("/auth/login") ||
    url.includes("/auth/refresh") ||
    url.includes("/auth/register") ||
    url.includes("/auth/logout")
  );
}

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;
    if (status === 401 && original && !original._retry && !isAuthEndpoint(original.url || "")) {
      original._retry = true;
      try {
        if (!refreshPromise) {
          refreshPromise = api
            .post("/auth/refresh")
            .then((r) => r.data)
            .finally(() => {
              refreshPromise = null;
            });
        }
        const data = await refreshPromise;
        if (data?.token) {
          setToken(data.token);
          original.headers = original.headers || {};
          original.headers.Authorization = `Bearer ${data.token}`;
        }
        return api(original);
      } catch (e) {
        setToken(null);
        if (typeof window !== "undefined") {
          const p = window.location.pathname;
          const publicPath = p === "/" || p.startsWith("/login") || p.startsWith("/register") || p.startsWith("/use-cases") || p.startsWith("/showcase") || p.startsWith("/pricing");
          if (!publicPath) {
            const next = encodeURIComponent(p + window.location.search);
            window.location.assign(`/login?next=${next}`);
          }
        }
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Download a file from an authenticated endpoint via blob.
export async function downloadFile(path, filename) {
  const res = await api.get(path, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export default api;
